/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore'
], function(
	_
) {

	const TRACK_TYPE_APPLICATION = 'application';
	const WIDGET_CATEGORY = 'InProduct';
	const WIDGET_DETAILS_PREFIX = 'custom.';

	const dataSourceInfoMap = {
		uploadedFile: 'custom.counts.uploadedFile',
		module: 'custom.counts.dataModule',
		package: 'custom.counts.fmPackage'
	};

	const widgetTypeMap = {
		data: 'custom.counts.data_widget',
		nondata : 'custom.counts.nondata_widget'
	};

	return class Segment {
		constructor(options) {
			this.dashboardApi = options.dashboardApi;
			this.instrumentationSvc = options.instrumentationSvc;
			this.logger = options.logger;
			this.infoCallback = undefined;
		}

		registerInfoCallback(cb) {
			this.infoCallback = cb;
		}

		unregisterInfoCallback() {
			this.infoCallback = null;
		}

		track(action, getInfoCallback) {
			try{
				const globalInfo = this.infoCallback ? this.infoCallback() : {};
				let suppliedInfo = getInfoCallback ? getInfoCallback() : {};
				suppliedInfo = {
					...globalInfo,
					...suppliedInfo
				};
				suppliedInfo.action = action;

				const trackType = suppliedInfo.trackType || TRACK_TYPE_APPLICATION;
				delete suppliedInfo.trackType;

				if (this._canTrack(trackType)) {
					const payload = this.getInfo(suppliedInfo, trackType);
					payload.action = action;
					payload.milestoneName =  payload.milestoneName ?  payload.milestoneName : `${action}_${payload.objectType}`;

					this.instrumentationSvc.track(payload);
				}

			} catch(e){
				this.logger.error(e);
			}
		}

		_canTrack(trackType) {
			return trackType === TRACK_TYPE_APPLICATION || this.dashboardApi.getApplicationName() !== 'explore';
		}

		getInfo(suppliedInfo = {}, trackType = TRACK_TYPE_APPLICATION) {
			return trackType === TRACK_TYPE_APPLICATION ? this._getApplicationInfo(suppliedInfo) : this._getWidgetInfo(suppliedInfo, trackType);
		}

		_getApplicationInfo(suppliedInfo = {}) {
			const detailsInfo = this._getApplicationDetailsInfo();
			return {
				type: suppliedInfo.type,
				objectType: this.dashboardApi.getApplicationName(),
				object: this._getBoardId(),
				...detailsInfo,
				...suppliedInfo
			};
		}

		_getWidgetInfo (suppliedInfo, trackType) {
			suppliedInfo = suppliedInfo || {};
			const details = {};

			if (suppliedInfo.details) {
				_.each(suppliedInfo.details, (value, name) => {
					details[WIDGET_DETAILS_PREFIX + name] = value;
				});
			}

			const payload = {
				type: suppliedInfo.type,
				objectType: trackType,
				category:  WIDGET_CATEGORY,
				...details
			};

			if (suppliedInfo.object) {
				payload.object = suppliedInfo.object;
			}

			payload.milestoneName = `${suppliedInfo.action}_${trackType}_in_${this.dashboardApi.getApplicationName()}`;

			return payload;
		}

		_getBoardId () {
			return this.dashboardApi.getDashboardInfo().boardId;
		}

		_getApplicationDetailsInfo() {
			const dataSourcesInfo = this._getApplicationDataSourcesInfo();
			const widgetsInfo = this._getApplicationWidgetsInfo();
			return {
				...dataSourcesInfo,
				...widgetsInfo,
				'custom.counts.tabs': this._getApplicationTabsCount()
			};
		}

		_getApplicationDataSourcesInfo () {
			const dataSourceList = this.dashboardApi.getFeature('DataSources').getDataSourceList();
			return Segment.getDataSourcesTypeCount(dataSourceList);
		}

		_getApplicationWidgetsInfo () {
			const widgets = this.dashboardApi.getCanvas().findContent({ type: 'widget' });
			return Segment.getWidgetsTypeCount(widgets);
		}

		_getApplicationTabsCount () {
			const tabs = this.dashboardApi.getCanvas().findContent({ type: 'container' });
			return tabs.length;
		}

		destroy() {
			this.dashboardApi = null;
			this.glassContext = null;
			this.instrumentationSvc = null;
		}

		static getDataSourcesTypeCount (dataSourceList = []) {
			const info = dataSourceList.reduce((acc, dataSource) => {
				const type = dataSource.getType();
				return {
					...acc,
					[dataSourceInfoMap[type]]: (acc[dataSourceInfoMap[type]] || 0) + 1
				};
			}, {});

			return info;
		}

		static getWidgetsTypeCount (widgets = []) {
			const info = widgets.reduce((acc, widget) => {
				const type = (widget.getType() === 'live' || widget.getType() === 'widget.live') ? 'data' : 'nondata';
				return {
					...acc,
					[widgetTypeMap[type]]: (acc[widgetTypeMap[type]] || 0) + 1
				};
			}, {});
			return info;
		}
	};
});
